<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import ContactAPI from 'dashboard/api/contacts';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { useContactBulkActions } from 'dashboard/composables/useContactBulkActions';
import ContactsCard from 'dashboard/components-next/Contacts/ContactsCard/ContactsCard.vue';
import ContactBulkLabelActions from 'dashboard/components-next/Contacts/ContactBulkLabelActions/ContactBulkLabelActions.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import WhatsappFlowEnroll from 'dashboard/components/widgets/conversation/WhatsappFlowEnroll.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
  totalContacts: { type: Number, default: 0 },
  sortAttr: { type: String, default: 'name' },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const expandedCardId = ref(null);
const showFlowEnrollModal = ref(false);
const selectedContactId = ref(null);
const hoveredCardId = ref(null);
const showLabelActions = ref(false);
const labelButtonRef = ref(null);
const dropdownStyle = ref({ top: '0px', left: '0px' });

const {
  selectedContactIds,
  hasSelected,
  selectedCount,
  isApplying,
  isFetchingAll,
  toggleContact,
  selectAll,
  selectAllIds,
  clearSelection,
  updateLabels,
} = useContactBulkActions();

const allSelected = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(c => selectedContactIds.value.has(c.id))
);

const isIndeterminate = computed(() => hasSelected.value && !allSelected.value);

const bulkCheckboxValue = computed({
  get: () => allSelected.value,
  set: shouldSelectAll => {
    if (shouldSelectAll) selectAll(props.contacts);
    else clearSelection();
  },
});

watch(
  [
    () => route.query.page,
    () => route.query.search,
    () => route.params.label,
    () => route.params.segmentId,
  ],
  () => {
    if (hasSelected.value) clearSelection();
  }
);

const showSelectAllBanner = computed(
  () => allSelected.value && selectedCount.value < props.totalContacts
);

const selectAllInView = async () => {
  isFetchingAll.value = true;
  try {
    const label = route.params.label || '';
    const search = route.query.search || '';
    const { data } = await ContactAPI.getIds(props.sortAttr, label, search);
    selectAllIds(data.ids);
  } catch {
    useAlert(t('BULK_ACTION.CONTACTS.SELECT_ALL_FAILED'));
  } finally {
    isFetchingAll.value = false;
  }
};

const selectableCardIds = computed(() => {
  if (hasSelected.value) return new Set(props.contacts.map(c => c.id));
  return new Set(hoveredCardId.value ? [hoveredCardId.value] : []);
});

const openLabelActions = async () => {
  await nextTick();
  const el = labelButtonRef.value?.$el ?? labelButtonRef.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    dropdownStyle.value = {
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
    };
  }
  showLabelActions.value = true;
};

const handleApplyLabels = async ({ labels, mode }) => {
  showLabelActions.value = false;
  await updateLabels({ labels, mode });
};

const updateContact = async updatedData => {
  try {
    await store.dispatch('contacts/update', updatedData);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};

const handleAddToFlow = contactId => {
  selectedContactId.value = contactId;
  showFlowEnrollModal.value = true;
};

const closeFlowEnrollModal = () => {
  showFlowEnrollModal.value = false;
  selectedContactId.value = null;
};
</script>

<template>
  <div class="relative isolate flex flex-col gap-4 px-6 pt-4 pb-6">
    <transition
      enter-active-class="transition-all duration-150 ease-out"
      leave-active-class="transition-all duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="hasSelected"
        class="sticky top-0 z-[100] flex items-center gap-3 px-4 py-2.5 rounded-lg bg-n-solid-2 border border-solid border-n-weak shadow-sm"
      >
        <div class="flex items-center gap-2">
          <Checkbox
            v-model="bulkCheckboxValue"
            :indeterminate="isIndeterminate"
          />
          <span class="text-sm font-medium text-n-slate-12 tabular-nums">
            {{ t('BULK_ACTION.CONTACTS.SELECTED', { count: selectedCount }) }}
          </span>
        </div>

        <div class="h-4 w-px bg-n-strong" />

        <div class="flex items-center gap-2">
          <Button
            ref="labelButtonRef"
            sm
            ghost
            slate
            icon="i-lucide-tags"
            :label="t('BULK_ACTION.CONTACTS.LABELS.ASSIGN_LABELS')"
            :is-loading="isApplying"
            class="!px-2"
            @click="openLabelActions"
          />
        </div>

        <div class="h-4 w-px bg-n-strong" />

        <Button
          sm
          ghost
          slate
          icon="i-lucide-x"
          :label="t('BULK_ACTION.CONTACTS.CLEAR_SELECTION')"
          class="!px-2"
          @click="clearSelection"
        />
      </div>
    </transition>

    <transition
      enter-active-class="transition-all duration-150 ease-out"
      leave-active-class="transition-all duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="showSelectAllBanner"
        class="flex items-center justify-center gap-1.5 py-2 px-4 rounded-lg bg-n-alpha-1 text-sm text-n-slate-11"
      >
        <span>
          {{
            t('BULK_ACTION.CONTACTS.PAGE_SELECTED', {
              count: props.contacts.length,
            })
          }}
        </span>
        <button
          type="button"
          class="font-medium text-n-brand hover:underline disabled:opacity-50"
          :disabled="isFetchingAll"
          @click="selectAllInView"
        >
          {{
            isFetchingAll
              ? t('BULK_ACTION.CONTACTS.SELECTING_ALL')
              : t('BULK_ACTION.CONTACTS.SELECT_ALL_IN_VIEW', {
                  count: props.totalContacts,
                })
          }}
        </button>
      </div>
    </transition>

    <ContactsCard
      v-for="contact in contacts"
      :id="contact.id"
      :key="contact.id"
      :name="contact.name"
      :email="contact.email"
      :thumbnail="contact.thumbnail"
      :phone-number="contact.phoneNumber"
      :additional-attributes="contact.additionalAttributes"
      :availability-status="contact.availabilityStatus"
      :is-expanded="expandedCardId === contact.id"
      :is-updating="isUpdating"
      :selectable="selectableCardIds.has(contact.id)"
      :is-selected="selectedContactIds.has(contact.id)"
      @toggle="toggleExpanded(contact.id)"
      @update-contact="updateContact"
      @show-contact="onClickViewDetails"
      @add-to-flow="handleAddToFlow"
      @select="toggleContact"
      @mouseenter="hoveredCardId = contact.id"
      @mouseleave="hoveredCardId = null"
    />
    <WhatsappFlowEnroll
      v-if="showFlowEnrollModal && selectedContactId"
      :contact-id="selectedContactId"
      @close="closeFlowEnrollModal"
    />
  </div>

  <Teleport to="body">
    <ContactBulkLabelActions
      v-if="showLabelActions"
      :style="dropdownStyle"
      @apply="handleApplyLabels"
      @close="showLabelActions = false"
    />
  </Teleport>
</template>
